import { pdf } from "@react-pdf/renderer";
import React from "react";
import { listFolderItems } from "../services/api-server/welldocs";
import { getTenantStore } from "../stores/tenantStore";
import { alertMessage } from "./alert";
import { isFolderNotFoundError } from "./sharepointErrors";

const createFileName = (data: any) => {
	return `${data.report_number}${
		data.project_code ? `_${data.project_code}` : ""
	}.pdf`;
};

const exportPdf = async (component: React.ReactElement, fileName: string) => {
	try {
		const blob = await pdf(component).toBlob();
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = fileName;
		link.click();

		URL.revokeObjectURL(url);
	} catch (error) {
		console.error("Failed to export PDF:", error);
	}
};

/**
 * Files to embed in a report export. A folder that no longer exists in
 * SharePoint yields no files rather than failing the export; the next save of
 * the report recreates it.
 */
export const fetchSharepointFiles = async (
	data: any,
	type: string,
	containerId: string
) => {
	let folderItems: any;
	try {
		folderItems = await listFolderItems(
			data.operator,
			data._id,
			data.folder_id,
			containerId,
			"x",
			type
		);
	} catch (error) {
		if (isFolderNotFoundError(error)) return [];
		throw error;
	}

	const reportFolderItems = Array.isArray(folderItems?.items)
		? folderItems.items
		: null;

	if (!reportFolderItems) return [];

	// Report-level uploads live in an "Additional documents" subfolder, except
	// for reports saved before that subfolder existed (and lesson learned
	// reports, which never use it) whose files sit directly in the report
	// folder. Export both so no attachment is dropped.
	const rootFiles = reportFolderItems.filter((item: any) => item?.file);
	const additionalDocumentsFolder = reportFolderItems.find(
		(item: any) => item?.folder && item.name === "Additional documents"
	);
	if (!additionalDocumentsFolder?.id) return rootFiles;

	let additionalDocumentsItems: any;
	try {
		additionalDocumentsItems = await listFolderItems(
			data.operator,
			data._id,
			additionalDocumentsFolder.id,
			containerId,
			"x",
			type
		);
	} catch (error) {
		if (isFolderNotFoundError(error)) return rootFiles;
		throw error;
	}
	const subfolderFiles = Array.isArray(additionalDocumentsItems?.items)
		? additionalDocumentsItems.items.filter((item: any) => item?.file)
		: [];

	return [...subfolderFiles, ...rootFiles];
};

export const handleExportReport = async (
	data: any,
	type: string,
	Template: React.ComponentType<any>
) => {
	const tenantList: any = getTenantStore().getTenantList();
	const containerId = tenantList.find(
		(tenant: any) => tenant.role_key === data.operator
	)?.container_id;

	// Stop the export if container Id not found
	if (!containerId) {
		alertMessage("error", "Container ID not found!");
		return;
	}

	try {
		const sharepointFiles = data.folder_id
			? await fetchSharepointFiles(data, type, containerId)
			: [];

		const pdfData = {
			...data,
			additional_files: sharepointFiles,
		};

		const fileName = createFileName(pdfData);
		exportPdf(<Template data={pdfData} />, fileName);
	} catch (error) {
		console.error("Failed to export report:", error);
		alertMessage(
			"error",
			"Unable to create the report PDF. Files could not be retrieved from SharePoint."
		);
		return;
	}
};

export const buildReportingHubFilePreviewUrl = (
	previewUrl: string,
	fileObject: any,
	containerId: string
) => {
	try {
		const fileId = fileObject?.id;
		if (!fileId) return null;
		const _record = encodeURIComponent(fileId);
		const _container = encodeURIComponent(containerId);
		return `${previewUrl}?record=${_record}&container=${_container}`;
	} catch {
		return null;
	}
};
